import axios from "axios";
import _ from "lodash";
import {uuid} from "vue-uuid";

const maxRetries = 10; // Maximum number of retries for sending a registration

const getDefaultState = () => {
    return {
        registrations: [], // Hold the outbox registrations
    }
}

export const registrationOutbox = {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        getRegistrations : (state) => {
          return state.registrations;
        },
        // Get registration outbox item IDs
        getItems: (state) => (numberOfItems) => {
            const ids = state.registrations.map(r => r.outbox && r.outbox.id).filter(Boolean);
            return numberOfItems ? ids.slice(0, numberOfItems) : ids;
        },
        // Pending ids: those retried less than maxRetries and not processing
        getPendingItems: (state) => (numberOfItems) => {
            const ids = state.registrations
                .filter(r => {
                    const retries = (r.outbox && r.outbox.retries) || 0;
                    const processing = (r.outbox && r.outbox.processing) || false;
                    return retries < maxRetries && !processing;
                })
                .map(r => r.outbox && r.outbox.id)
                .filter(Boolean);
            return numberOfItems ? ids.slice(0, numberOfItems) : ids;
        }
    },
    actions: {
        // Add a new registration to the outbox
        add({ commit }, registration) {
          return new Promise((resolve) => {
            try {
              // Make sure we clone the object, otherwise the object in the store might be mutated from outside.
              const registrationClone = _.cloneDeep(registration);
              // Make sure the 'outbox' property exists. We need this otherwise it won't be reactive.
              _.defaults(registrationClone, { outbox: { retries: 0, last_error: null, processing: false } });
              commit("add", registrationClone);
              resolve({ success: true});
            } catch (e) {
              resolve({ success: false, error: e && e.message ? e.message : e });
            }
          })
        },
        // Delete a registration from the outbox by its ID
        delete({ commit }, { registrationId }) {
            commit("delete", registrationId)
        },
        // Delete all outbox registrations
        deleteAll({ commit }) {
            commit("deleteAll")
        },
        unmarkProcessing({ commit }, { registrationId }) {
          commit('unmarkProcessing', registrationId);
        },
      /**
         * Send numberOfItems registrations to the server.
         * We use ID lists from the getters and then process each ID. This avoids
         * iterating live object arrays and sharing references between concurrent callers.
         */
        async syncRegistrationsToServer ({ dispatch, getters }, {numberOfItems = 10} = {}) {
          // Get a stable list of ids
          let ids = getters.getItems(numberOfItems);
          if (!ids || ids.length === 0) {
            return;
          }
          for (const id of ids) {
            await dispatch('sendRegistrationToServer', { registrationId: id });
          }
        },
        /**
         * Send pending registrations to the server.
         */
        async syncPendingRegistrationsToServer ({ dispatch, getters }, {numberOfItems = 10} = {}) {
          const ids = getters.getPendingItems(numberOfItems);
          if (!ids || ids.length === 0) {
            return;
          }
          for (const id of ids) {
            await dispatch('sendRegistrationToServer', { registrationId: id });
          }
        },
      /**
       * Send a single registration to the server.
       *
       * @param commit
       * @param dispatch
       * @param registration
       * @param timeout Timeout in milliseconds how long to wait for a server response.
       * @returns {Promise<{status: boolean, message: string}>}
       */
        async sendRegistrationToServer ({ commit, state }, { registrationId, timeout = 30000 } = {}) {
          // Find current item in state by id
          const registration = state.registrations.find(r => r.outbox && r.outbox.id === registrationId);
          if (!registration) {
            return { status: false, message: 'Registration not found in outbox.' };
          }

          // If it's already being processed by another caller, skip it.
          if (registration.outbox && registration.outbox.processing) {
            return { status: false, message: 'Already sending to server' };
          }

          // Mark as processing (synchronous mutation)
          commit('markProcessing', registrationId);
          // Increment the attempt counter (mutation)
          commit('incrementRetries', registrationId);

          try {
            const form_id = registration.form_id;
            // Clone the registration object to avoid any races where state mutations
            // change the object while axios is serializing it.
            const payload = _.cloneDeep(registration);
            const response = await axios.post(`api/form/${form_id}/create-registration`, payload, { timeout });

            if (response && response.status === 201) {
              // Successful: remove it from the outbox
              commit('delete', registrationId);
              return { status: true, message: 'Registratie is opgeslagen in de cloud.' };
            }

            // Not successful: log error on the item
            commit('logError', { registrationId, error: `Registratie is niet verstuurd. HTTP status code: ${response && response.status}` });
            return { status: false, message: `Registratie is niet verstuurd. HTTP status code: ${response && response.status}` };
          } catch (error) {
            commit('logError', { registrationId, error: `Registratie is niet verstuurd. ${error}` });
            return { status: false, message: `Registratie is niet verstuurd. ${error}` };
          } finally {
            // Always unmark processing (item might have been deleted already; unmark safely)
            commit('unmarkProcessing', registrationId);
          }
        },
    },
    mutations: {
        // Add a new registration
        add(state, registration) {
          // Ensure outbox exists and has an id and processing flag
          if (!registration.outbox) registration.outbox = {};
          if (!registration.outbox.id) {
            registration.outbox.id = uuid.v4()
          }
          if (registration.outbox.retries == null) registration.outbox.retries = 0;
          if (registration.outbox.processing == null) registration.outbox.processing = false;
          state.registrations.push(registration)
        },
        // Delete a registration from the outbox by id
        delete(state, registrationId) {
          if (!registrationId) return;
          const index = state.registrations.findIndex(r => r.outbox && r.outbox.id === registrationId);
          if (index !== -1) state.registrations.splice(index, 1);
        },
        // Delete all outbox registrations
        deleteAll(state) {
            Object.assign(state, getDefaultState())
        },
        incrementRetries(state, registrationId) {
          const item = state.registrations.find(r => r.outbox && r.outbox.id === registrationId);
          if (item) {
            _.defaults(item, { outbox: {} }); // Create outbox property if it doesn't exist
            item.outbox.retries = (item.outbox.retries || 0) + 1;
          }
        },
        logError(state, { registrationId, error }) {
          const item = state.registrations.find(r => r.outbox && r.outbox.id === registrationId);
          if (item) {
            _.defaults(item, { outbox: {} }); // Create outbox property if it doesn't exist
            item.outbox.last_error = error;
          }
        },
        // Mark an item as being processed to avoid duplicate in-flight sends
        markProcessing(state, registrationId) {
          const item = state.registrations.find(r => r.outbox && r.outbox.id === registrationId);
          if (item) {
            _.defaults(item, { outbox: {} });
            item.outbox.processing = true;
          }
        },
        // Unmark processing. If item was deleted, this is a no-op.
        unmarkProcessing(state, registrationId) {
          const item = state.registrations.find(r => r.outbox && r.outbox.id === registrationId);
          if (item) {
            _.defaults(item, { outbox: {} });
            item.outbox.processing = false;
          }
        },
    }
};